import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import toast, { Renderable, ValueOrFunction } from 'react-hot-toast'
import { format } from 'date-fns'
import { ASSET_DECIMALS } from './Constants'
import type { ManagedBalanceLedger } from '../state/Radom'

export const shortAddress = (address: string, decimals = 6): string => {
  if (!address) {
    return ''
  }

  return address.substring(0, decimals) + '...' + address.slice(address.length - decimals)
}

export const cutString = (str: string, decimals = 6): string => {
  if (!str) {
    return ''
  }

  return str.substring(0, decimals) + '...'
}

export const SecondsToPaymentPeriod = (seconds: number): string => {
  const intervals = {
    1: 'Second',
    60: 'Minute',
    [60 * 60]: 'Hour',
    [60 * 60 * 24]: 'Day',
    [60 * 60 * 24 * 7]: 'Week',
    [60 * 60 * 24 * 30]: 'Month',
    [60 * 60 * 24 * 30 * 3]: 'Quarter',
    [60 * 60 * 24 * 30 * 6]: 'Six Months',
    [60 * 60 * 24 * 365]: 'Year'
  }

  if (intervals[seconds]) {
    return intervals[seconds]
  }

  if (seconds > 60 * 60 * 24 * 365) {
    const years = (seconds / (60 * 60 * 24 * 365)).toFixed(1)
    return `${years} Years`
  }
  if (seconds > 60 * 60 * 24 * 30) {
    const months = (seconds / (60 * 60 * 24 * 30)).toFixed(1)
    return `${months} Months`
  }
  if (seconds > 60 * 60 * 24) {
    const days = (seconds / (60 * 60 * 24)).toFixed(1)
    return `${days} Days`
  }
  if (seconds > 60 * 60) {
    const hours = (seconds / (60 * 60)).toFixed(1)
    return `${hours} Hours`
  }
  if (seconds > 60) {
    const minutes = (seconds / 60).toFixed(1)
    return `${minutes} Minutes`
  }

  return `${seconds} Seconds`
}

export function convertDateToUTC(date: Date): Date {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  )
}

export function errorToast(message: string): void {
  toast.error(message, { position: 'top-center', style: { fontSize: 14, boxSizing: 'content-box', overflowWrap: 'anywhere' } })
}

export function extractErrorMessage(error: any, defaultMessage: string): string {
  const message = error?.response?.data?.message ?? error?.response?.data?.error ?? error?.message ?? error?.toString()
  return typeof message === 'string' && message.trim() !== '' ? message : defaultMessage
}

export function successToast(message: string): void {
  toast.success(message, { position: 'top-center', style: { fontSize: 14, boxSizing: 'content-box', overflowWrap: 'anywhere' } })
}

export function infoToast(message: string): void {
  toast(message, { position: 'top-center', style: { fontSize: 14, boxSizing: 'content-box', overflowWrap: 'anywhere' } })
}

export async function promiseToast<T>(
  promise: Promise<T>,
  loading: string,
  success: string,
  error: ValueOrFunction<Renderable, any>
): Promise<T> {
  return await toast.promise(
    promise,
    { loading, success, error }, {
      position: 'top-center',
      style: {
        fontSize: 14
      }
    })
}

export const TokenCurrencies = ['USDT', 'USDC', 'EURC', 'ETH']

export const formatCurrency = (amount: number | string, currency: string, decimals: number = 2): string => {
  const effectiveDecimals = decimals !== undefined ? decimals : 2
  const numericAmount = typeof amount === 'number' ? amount : Number(amount)

  if (!TokenCurrencies.includes(currency)) {
    try {
      const options: Intl.NumberFormatOptions = {
        style: 'currency',
        currency,
        minimumFractionDigits: effectiveDecimals,
        maximumFractionDigits: effectiveDecimals
      }
      const formattedAmount = Intl.NumberFormat('en-US', options).format(numericAmount).replace(/(\.\d*[1-9])0+$|\.0*$/, '$1')

      return formattedAmount
    } catch (err) {
      return Intl.NumberFormat('en-US', { minimumFractionDigits: effectiveDecimals, maximumFractionDigits: effectiveDecimals }).format(Number(amount))
    }
  }

  const tokenFormattedAmount = numericAmount.toFixed(6).replace(/(\.\d*[1-9])0+$|\.0*$/, '$1')

  return `${tokenFormattedAmount} ${currency}`
}

export function simpleEmailValidation(address: string): boolean {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return regex.test(address)
}

export function sanitizeName(input: string): string {
  let clean = input
  clean = clean.replace(/[^a-zA-Z ]/g, '')
  clean = clean.replace(/\s\s+/g, ' ')
  clean = clean.replace(/^\s+/, '')
  return clean
}

export function sanitizeAddress(input: string): string {
  let clean = input
  clean = clean.replace(/[^a-zA-Z0-9 /.-]/g, '')
  clean = clean.replace(/([ /.-])\1+/g, '$1')
  clean = clean.replace(/^[ /.-]+/, '')
  return clean
}

export function sanitizeFirstName(input: string): string {
  return input.replace(/[^a-zA-Z]/g, '')
}

const KEYS_KEPT_ON_SIGN_OUT = ['radomTheme']

export function clearSessionLocalStorage(): void {
  const kept = KEYS_KEPT_ON_SIGN_OUT
    .map((key) => [key, localStorage.getItem(key)] as const)
    .filter((entry): entry is readonly [string, string] => entry[1] !== null)

  localStorage.clear()

  for (const [key, value] of kept) {
    localStorage.setItem(key, value)
  }
}


function setLocalStorageItemWithEvent(key: string, value?: string): void {
  if (!value) {
    localStorage.removeItem(key)
  } else {
    localStorage.setItem(key, value)
  }

  dispatchEvent(new Event('localStorageItemSet'))
}

const TEST_MODE_KEY = 'testMode'
export function useTestMode(): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [testMode, setTestMode] = useState(!!localStorage.getItem(TEST_MODE_KEY))

  useEffect(() => {
    addEventListener('localStorageItemSet', () => {
      const newTestMode = !!localStorage.getItem(TEST_MODE_KEY)
      setTestMode(newTestMode)
    })
  }, [])

  useEffect(() => {
    setLocalStorageItemWithEvent(TEST_MODE_KEY, testMode ? 'true' : undefined)
  }, [testMode])

  return [testMode, setTestMode]
}

export function nYearsAgo(yearsAgo: number): Date {
  const today = new Date()
  return new Date(today.getFullYear() - yearsAgo, today.getMonth(), today.getDate())
}

export function unitFn(_: any): any { }
export function isNotEmpty(v: string): boolean { return !!v && !!v.trim() }

export interface DateParts {
  datePart: string
  timePart: string
}

export function splitDateTime(d: Date): DateParts {
  return {
    datePart: format(d, 'yyyy-MM-dd'),
    timePart: format(d, 'HH:mm:ss')
  }
}

export function highlightInvalidFields(container?: HTMLElement): void {
  const root = container ?? document
  const invalid = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input:invalid, textarea:invalid, select:invalid')
  invalid.forEach(el => {
    if (typeof (el as any).reportValidity === 'function') {
      (el as any).reportValidity()
    }
    el.classList.add('invalid-highlight')
    setTimeout(() => el.classList.remove('invalid-highlight'), 1000)
  })
}

export const asSimpleDateTime = (d: string): string => format(new Date(d), 'yyyy-MM-dd HH:mm:ss')

export const trimZeros = (number: string | number): string => {
  const num = number.toString()
  return num.includes('.')
    ? num.replace(/(?:\.0*|(\.\d+?)0+)$/, '$1')
    : num
}

export const downloadBlob = (blob: Blob, filename: string): HTMLAnchorElement => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')

  a.href = url
  a.download = filename || 'download'

  const clickHandler = (): void => {
    setTimeout(() => {
      URL.revokeObjectURL(url)
      removeEventListener('click', clickHandler)
    }, 150)
  }

  a.addEventListener('click', clickHandler, false)
  a.click()

  return a
}

export const getExportFileName = (type: string): string => {
  const now = new Date()
  const dateStr = now.toISOString().slice(0, 10)
  const timeStr = now.toTimeString().slice(0, 5).replace(':', '-')
  return `${type}_${dateStr}T${timeStr}.csv`
}

export const roundAssetAmount = (
  ticker: string,
  amount: number | string
): string => {
  const amountNumber = Number(amount)
  const decimals = ASSET_DECIMALS[ticker]

  if (!decimals) {
    return trimZeros(amount.toString())
  }

  return trimZeros(amountNumber.toFixed(decimals))
}

const normalizeAssetAmountParts = (amount: string): [string, string] => {
  const trimmed = amount.trim()
  if (!trimmed) {
    return ['0', '']
  }

  const [intPart, fracPart = ''] = trimmed.split('.')
  const normalizedInt = (intPart || '0').replace(/^0+(?=\d)/, '') || '0'
  return [normalizedInt, fracPart]
}

export const compareAssetAmountStrings = (left: string, right: string): number => {
  const [leftInt, leftFrac] = normalizeAssetAmountParts(left)
  const [rightInt, rightFrac] = normalizeAssetAmountParts(right)

  if (leftInt.length !== rightInt.length) {
    return leftInt.length > rightInt.length ? 1 : -1
  }
  if (leftInt !== rightInt) {
    return leftInt > rightInt ? 1 : -1
  }

  const maxFracLen = Math.max(leftFrac.length, rightFrac.length)
  const leftFracPadded = leftFrac.padEnd(maxFracLen, '0')
  const rightFracPadded = rightFrac.padEnd(maxFracLen, '0')
  if (leftFracPadded === rightFracPadded) {
    return 0
  }
  return leftFracPadded > rightFracPadded ? 1 : -1
}

export const floorAssetAmount = (
  ticker: string | undefined,
  amount: number | string
): string => {
  const amountStr = typeof amount === 'string' ? amount.trim() : trimZeros(amount.toString())
  if (!amountStr) {
    return '0'
  }

  const decimals = ticker ? ASSET_DECIMALS[ticker.toUpperCase()] : undefined
  if (decimals === undefined) {
    return trimZeros(amountStr)
  }

  const [intPart, fracPart = ''] = amountStr.split('.')
  if (fracPart.length <= decimals) {
    return trimZeros(amountStr)
  }

  return trimZeros(`${intPart}.${fracPart.slice(0, decimals)}`)
}

const DEFAULT_ASSET_DECIMALS = 8

export const STABLE_SWAP_TICKERS = new Set(['USDC', 'USDT'])

export const isStableSwapTicker = (ticker?: string | null): boolean =>
  !!ticker && STABLE_SWAP_TICKERS.has(ticker.toUpperCase())

export const formatAssetAmount = (
  amount: number | string | undefined | null,
  ticker?: string,
  fallbackDecimals: number = DEFAULT_ASSET_DECIMALS
): string => {
  if (amount === undefined || amount === null || amount === '') {
    return '--'
  }

  const num = typeof amount === 'number' ? amount : Number(amount)
  if (!Number.isFinite(num)) {
    return '--'
  }
  if (num === 0) {
    return '0'
  }

  const tickerKey = ticker?.toUpperCase()
  const decimals = tickerKey && ASSET_DECIMALS[tickerKey] !== undefined
    ? ASSET_DECIMALS[tickerKey]
    : fallbackDecimals

  const trimmed = trimZeros(num.toFixed(decimals))
  const [intPart, decPart] = trimmed.split('.')
  const sign = intPart.startsWith('-') ? '-' : ''
  const intDigits = sign ? intPart.slice(1) : intPart
  const formattedInt = sign + Intl.NumberFormat('en-US').format(Number(intDigits))

  return decPart ? `${formattedInt}.${decPart}` : formattedInt
}

export const formatRateAmount = (
  amount: number | string | undefined | null,
  significantDigits: number = 8
): string => {
  if (amount === undefined || amount === null || amount === '') {
    return '--'
  }

  const num = typeof amount === 'number' ? amount : Number(amount)
  if (!Number.isFinite(num)) {
    return '--'
  }
  if (num === 0) {
    return '0'
  }

  const abs = Math.abs(num)
  const integerDigits = abs >= 1 ? Math.floor(Math.log10(abs)) + 1 : 0
  const leadingZeros = abs < 1 ? Math.max(-Math.floor(Math.log10(abs)) - 1, 0) : 0
  const decimals = Math.min(Math.max(significantDigits - integerDigits, 0) + leadingZeros, 18)

  const trimmed = trimZeros(num.toFixed(decimals))
  const [intPart, decPart] = trimmed.split('.')
  const sign = intPart.startsWith('-') ? '-' : ''
  const intDigits = sign ? intPart.slice(1) : intPart
  const formattedInt = sign + Intl.NumberFormat('en-US').format(Number(intDigits))

  return decPart ? `${formattedInt}.${decPart}` : formattedInt
}

/**
 * Folds the legs a transaction took through one balance into a single
 * before-and-after: per unified asset for organisations on the unified
 * treasury, per network row otherwise.
 */
export const unifyLedger = (ledger: ManagedBalanceLedger[]): ManagedBalanceLedger[] => {
  const sameBalance = (a: ManagedBalanceLedger, b: ManagedBalanceLedger): boolean =>
    a.unified || b.unified
      ? !!a.unified && !!b.unified && a.asset === b.asset
      : a.network === b.network && a.token === b.token
  return ledger.reduce<ManagedBalanceLedger[]>((acc, curr) => {
    const index = acc.findIndex(item => sameBalance(item, curr))
    if (index === -1) {
      acc.push(curr)
      return acc
    }
    const isMinus = acc[index].balanceBefore > acc[index].balanceAfter && curr.balanceBefore > curr.balanceAfter
    let balanceBefore: string = ''
    let balanceAfter: string = ''
    if (isMinus) {
      balanceBefore = Math.max(Number(acc[index].balanceBefore), Number(curr.balanceBefore)).toString()
      balanceAfter = Math.min(Number(acc[index].balanceAfter), Number(curr.balanceAfter)).toString()
    } else {
      balanceBefore = Math.min(Number(acc[index].balanceBefore), Number(curr.balanceBefore)).toString()
      balanceAfter = Math.max(Number(acc[index].balanceAfter), Number(curr.balanceAfter)).toString()
    }
    acc[index] = { ...acc[index], balanceBefore, balanceAfter }
    return acc
  }, [])
}

export const ROUTES = {
  ROOT: '/',
  MASSIVE_PAYOUTS: '/mass_payouts',
  MASSIVE_PAYOUT_DETAIL: '/mass_payout/:id',
  CREATE_MASSIVE_PAYOUT: '/mass_payouts/create',
  PAYOUTS: '/payouts',
  PAYOUT_DETAIL: '/payout/:id',
  CREATE_PAYOUT: '/payouts/create',

  getMassivePayoutDetailRoute: (item1?: string): string => {
    const parts = [item1].filter(Boolean)
    return `${ROUTES.MASSIVE_PAYOUTS.slice(0, -1)}${parts.length ? '/' + parts.join('/') : ''}`
  },
  getPayoutDetailRoute: (item1?: string): string => {
    const parts = [item1].filter(Boolean)
    return `${ROUTES.PAYOUTS.slice(0, -1)}${parts.length ? '/' + parts.join('/') : ''}`
  }

}
